import {
  SIDEBAR_CATEGORIES,
  type SidebarCategoryId,
  type WorkflowRegistryFields,
} from './Sidebar/sidebarPalette';

export type AgentKindId =
  | 'data-analyst'
  | 'email-composer'
  | 'research-agent'
  | 'report-generator'
  | 'approval-router'
  | 'custom';

export type AgentOperationKind = 'auto' | 'llm' | 'mcp' | 'action' | 'emit';

export type AgentOperation = {
  id: string;
  name: string;
  kind: AgentOperationKind;
  eventName?: string;
  eventPayload?: string;
};

export type LlmBackboneId = 'claude-sonnet' | 'gpt-4o' | 'gemini';

export const LLM_BACKBONE_OPTIONS: { id: LlmBackboneId; label: string }[] = [
  { id: 'claude-sonnet', label: 'Sonnet' },
  { id: 'gpt-4o', label: 'GPT-4o' },
  { id: 'gemini', label: 'Gemini' },
];

export const AGENT_SOFT_OP_LIMIT = 8;

export const AGENT_TYPE_META: Record<
  AgentKindId,
  { title: string; defaultObjective: string; defaultSchema: string }
> = {
  'data-analyst': {
    title: 'Data Analyst',
    defaultObjective: 'Analyze lead data and score fit based on enrichment, intent, and engagement.',
    defaultSchema: `{
  "score": "number",
  "grade": "A | B | C | D",
  "report": "string",
  "reasoning": "string"
}`,
  },
  'email-composer': {
    title: 'Email Composer',
    defaultObjective: 'Draft contextual emails that match tone, audience, and campaign goals.',
    defaultSchema: `{
  "subject": "string",
  "body": "string"
}`,
  },
  'research-agent': {
    title: 'Research Agent',
    defaultObjective: 'Plan research, gather sources, and synthesize findings with citations.',
    defaultSchema: `{
  "summary": "string",
  "sources": "string[]"
}`,
  },
  'report-generator': {
    title: 'Report Generator',
    defaultObjective: 'Create structured reports with sections, charts references, and conclusions.',
    defaultSchema: `{
  "title": "string",
  "sections": "object[]"
}`,
  },
  'approval-router': {
    title: 'Approval Router',
    defaultObjective: 'Evaluate inputs against policies and route to the correct approval path.',
    defaultSchema: `{
  "route": "string",
  "confidence": "number"
}`,
  },
  custom: {
    title: 'Custom Agent',
    defaultObjective: 'Describe what this agent should accomplish.',
    defaultSchema: `{
  "result": "string"
}`,
  },
};

function opTemplates(): Record<AgentKindId, Omit<AgentOperation, 'id'>[]> {
  return {
    'data-analyst': [
      { name: 'Read context', kind: 'auto' },
      { name: 'Analyze patterns', kind: 'llm' },
      { name: 'Score lead', kind: 'llm' },
      { name: 'Generate report', kind: 'llm' },
      {
        name: 'lead.scored',
        kind: 'emit',
        eventName: 'lead.scored',
        eventPayload: '{\n  "score": "{{agent.score}}"\n}',
      },
    ],
    'email-composer': [
      { name: 'Read context', kind: 'auto' },
      { name: 'Draft email', kind: 'llm' },
      { name: 'Refine tone', kind: 'llm' },
    ],
    'research-agent': [
      { name: 'Plan research', kind: 'llm' },
      { name: 'Gather data', kind: 'mcp' },
      { name: 'Synthesize', kind: 'llm' },
    ],
    'report-generator': [
      { name: 'Read context', kind: 'auto' },
      { name: 'Outline report', kind: 'llm' },
      { name: 'Render sections', kind: 'llm' },
    ],
    'approval-router': [
      { name: 'Load policy', kind: 'auto' },
      { name: 'Evaluate branches', kind: 'llm' },
      { name: 'Route decision', kind: 'mcp' },
    ],
    custom: [
      { name: 'Read context', kind: 'auto' },
      { name: 'Process', kind: 'llm' },
    ],
  };
}

export const AGENT_TEMPLATES = opTemplates();

export function isAgentsCategory(category: SidebarCategoryId): boolean {
  return category === SIDEBAR_CATEGORIES.AGENTS;
}

export function slugifyAgentNodeName(raw: string): string {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return s || 'agent';
}

export function paletteTypeToAgentKind(paletteType: string): AgentKindId | null {
  const map: Record<string, AgentKindId> = {
    Agent_DataAnalyst: 'data-analyst',
    Agent_EmailComposer: 'email-composer',
    Agent_DataResearcher: 'research-agent',
    Agent_ReportGenerator: 'report-generator',
    Agent_ApprovalRouter: 'approval-router',
  };
  return map[paletteType] ?? null;
}

/** Kind to use when placing an agent from the sidebar (mapped type or custom template). */
export function agentKindFromPaletteType(paletteType: string): AgentKindId {
  return paletteTypeToAgentKind(paletteType) ?? 'custom';
}

export function operationsFromAgentKind(kind: AgentKindId): AgentOperation[] {
  const tpl = AGENT_TEMPLATES[kind] ?? AGENT_TEMPLATES.custom;
  return tpl.map((o, i) => ({
    ...o,
    id: `op-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
  }));
}

export type FlowAgentPersistedData = {
  label: string;
  paletteType: string;
  agentKind: AgentKindId;
  nodeName: string;
  backbone: LlmBackboneId;
  objective: string;
  /** When set, overrides auto-derived edge label in UI */
  inputMappingOverride: string | null;
  operations: AgentOperation[];
  outputSchemaJson: string;
  eventSectionCollapsed: boolean;
  runStatus?: 'idle' | 'running' | 'success' | 'failed';
} & WorkflowRegistryFields;

export function createAgentDataForKind(
  kind: AgentKindId,
  paletteHint: { type: string; label: string },
  registry?: WorkflowRegistryFields
): FlowAgentPersistedData {
  const meta = AGENT_TYPE_META[kind];
  const fromPalette = paletteTypeToAgentKind(paletteHint.type);
  const usePaletteLabel =
    fromPalette === kind || (kind === 'custom' && fromPalette == null);
  return {
    label: usePaletteLabel ? paletteHint.label : meta.title,
    paletteType: paletteHint.type,
    agentKind: kind,
    nodeName: slugifyAgentNodeName(usePaletteLabel ? paletteHint.label : meta.title),
    backbone: 'gpt-4o',
    objective: meta.defaultObjective,
    inputMappingOverride: null,
    operations: operationsFromAgentKind(kind),
    outputSchemaJson: meta.defaultSchema,
    eventSectionCollapsed: true,
    runStatus: 'idle',
    ...registry,
  };
}

export function backboneLabel(id: LlmBackboneId): string {
  return LLM_BACKBONE_OPTIONS.find((b) => b.id === id)?.label ?? id;
}

export function buildAgentOutputLines(nodeName: string, schemaJson: string): string {
  const lines: string[] = [];
  try {
    const o = JSON.parse(schemaJson) as Record<string, string>;
    if (o && typeof o === 'object') {
      for (const key of Object.keys(o)) {
        lines.push(`{{ ${nodeName}.${key} }}`);
      }
    }
  } catch {
    lines.push(`{{ ${nodeName}.output }}`);
  }
  if (lines.length === 0) lines.push(`{{ ${nodeName}.output }}`);
  return lines.join('\n');
}

export function operationBadgeClass(kind: AgentOperationKind): string {
  switch (kind) {
    case 'auto':
      return 'agent-node__badge--auto';
    case 'llm':
      return 'agent-node__badge--llm';
    case 'mcp':
      return 'agent-node__badge--mcp';
    case 'action':
      return 'agent-node__badge--action';
    case 'emit':
      return 'agent-node__badge--emit';
    default:
      return 'agent-node__badge--auto';
  }
}

export function operationBadgeText(kind: AgentOperationKind): string {
  if (kind === 'llm') return 'llm';
  if (kind === 'emit') return 'emit';
  return kind;
}
